/* eslint-disable no-underscore-dangle */

import L from 'leaflet';
import { includes } from 'lodash';

const TOP_OFFSET = 10;
const CONTAINER_HEIGHT = 180;
const CONTAINER_WIDTH = 200;
const TIP_HEIGHT = 40;
const CONTAINER_Y_OFFSET = 28;
const CONTAINER_X_OFFSET = 40;
const RESIZER_SIZE = 25;

const CONTAINER_STYLE = `
  overflow-y: scroll;
  margin: 13px 19px;
  line-height: 1.4;
`;

const WRAPPER_STYLE = `
  position: absolute;
  padding: 1px;
  text-align: left;
  border-radius: 12px;
  background: white;
  color: #333;
  -webkit-box-shadow: 0 3px 14px rgb(255, 255, 255, 0.4);
  box-shadow: 0 3px 14px rgb(255, 255, 255, 0.4);
  `;

const CLOSE_BUTTON_STYLE = `
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 4px 4px 0 0;
  border: none;
  text-align: center;
  width: 18px;
  height: 14px;
  font: 16px/14px Tahoma, Verdana, sans-serif;
  color: #c3c3c3;
  text-decoration: none;
  font-weight: bold;
  background: transparent;
  cursor: pointer;
  z-index: 1;
  `;

const CURSOR_TYPES = {
  TOP: 'ns-resize',
  LEFT: 'w-resize',
  RIGHT: 'e-resize',
  TOP_LEFT: 'nwse-resize',
  TOP_RIGHT: 'nesw-resize'
};

const TOP_LEFT_RESIZER_STYLE = `
  position: absolute;
  top: ${-TOP_OFFSET}px;
  border: none;
  width: ${RESIZER_SIZE}px;
  height: ${RESIZER_SIZE}px;
  cursor: ${CURSOR_TYPES.TOP_LEFT};
`;

const TOP_RIGHT_RESIZER_STYLE = `
  position: absolute;
  top: ${-TOP_OFFSET}px;
  left: calc(100% - ${RESIZER_SIZE}px);
  border: none;
  width: ${RESIZER_SIZE}px;
  height: ${RESIZER_SIZE}px;
  cursor: ${CURSOR_TYPES.TOP_RIGHT};
`;

const TOP_RESIZER_STYLE = `
  position: absolute;
  top: ${-TOP_OFFSET}px;
  left: ${RESIZER_SIZE}px;
  width: calc(100% - ${2 * RESIZER_SIZE}px);
  border: none;
  height: 18px;
  cursor: ${CURSOR_TYPES.TOP};
`;

const LEFT_RESIZER_STYLE = `
  position: absolute;
  top: ${-TOP_OFFSET + RESIZER_SIZE}px;
  width: ${RESIZER_SIZE}px;
  height: calc(100% - ${RESIZER_SIZE}px);
  border: none;
  cursor: ${CURSOR_TYPES.LEFT};
`;

const RIGHT_RESIZER_STYLE = `
  position: absolute;
  top: ${-TOP_OFFSET + RESIZER_SIZE}px;
  left: calc(100% - ${RESIZER_SIZE}px);
  width: ${RESIZER_SIZE}px;
  height: calc(100% - ${RESIZER_SIZE}px);
  border: none;
  cursor: ${CURSOR_TYPES.RIGHT};
`;

let generalWidth = 0;
let generalHeight = 0;

L.CustomPopup = L.Popup.extend({
  _initLayout() {
    const prefix = 'leaflet-popup';
    this._container = L.DomUtil.create('div', `${prefix} leaflet-zoom-animated`);
    const container = this._container;
    this._wrapper = L.DomUtil.create('div', '', container);
    const wrapper = this._wrapper;
    wrapper.style.cssText = WRAPPER_STYLE;
    this._contentNode = L.DomUtil.create('div', '', wrapper);
    this._contentNode.style.cssText = CONTAINER_STYLE;

    L.DomEvent.disableClickPropagation(wrapper);
    L.DomEvent.disableScrollPropagation(this._contentNode);
    L.DomEvent.on(wrapper, 'contextmenu', L.DomEvent.stopPropagation);

    this._tipContainer = L.DomUtil.create('div', `${prefix}-tip-container`, container);
    this._tip = L.DomUtil.create('div', `${prefix}-tip`, this._tipContainer);

    if (this.options.closeButton) {
      this._closeButton = L.DomUtil.create('a', '', wrapper);
      const closeButton = this._closeButton;
      closeButton.innerHTML = '&#215;';
      closeButton.style.cssText = CLOSE_BUTTON_STYLE;
      L.DomEvent.on(closeButton, 'click', this._onCloseButtonClick, this);
    }

    if (!L.Browser.mobile) {
      const topResizer = L.DomUtil.create('div', '', wrapper);
      topResizer.style.cssText = TOP_RESIZER_STYLE;

      const topLeftResizer = L.DomUtil.create('div', '', wrapper);
      topLeftResizer.style.cssText = TOP_LEFT_RESIZER_STYLE;

      const topRightResizer = L.DomUtil.create('div', '', wrapper);
      topRightResizer.style.cssText = TOP_RIGHT_RESIZER_STYLE;

      const leftResizer = L.DomUtil.create('div', '', wrapper);
      leftResizer.style.cssText = LEFT_RESIZER_STYLE;

      const rightResizer = L.DomUtil.create('div', '', wrapper);
      rightResizer.style.cssText = RIGHT_RESIZER_STYLE;

      L.DomEvent.disableClickPropagation(topResizer);
      L.DomEvent.disableClickPropagation(topLeftResizer);
      L.DomEvent.disableClickPropagation(topRightResizer);
      L.DomEvent.disableClickPropagation(leftResizer);
      L.DomEvent.disableClickPropagation(rightResizer);

      topResizer.addEventListener('mousedown', event => this.onMouseDown(event));
      topLeftResizer.addEventListener('mousedown', event => this.onMouseDown(event));
      topRightResizer.addEventListener('mousedown', event => this.onMouseDown(event));
      leftResizer.addEventListener('mousedown', event => this.onMouseDown(event));
      rightResizer.addEventListener('mousedown', event => this.onMouseDown(event));
    }
    document.getElementsByClassName('leaflet-container')[0].addEventListener('mouseleave', e => {
      this._onCloseButtonClick(e);
    });
  },

  _updateLayout() {
    this._contentNode.style.whiteSpace = 'nowrap';

    this._contentNode.style.height = `${CONTAINER_HEIGHT}px`;

    const originalHeight = Math.max(this._wrapper.offsetHeight, generalHeight);
    const originalWidth = Math.max(this._wrapper.offsetWidth, generalWidth);

    this._wrapper.style.top = `${-originalHeight}px`;
    this._wrapper.style.left = `${-originalWidth / 2}px`;

    this._contentNode.style.height = `${originalHeight - CONTAINER_Y_OFFSET}px`;
    this._contentNode.style.width = `${originalWidth - CONTAINER_X_OFFSET}px`;
  },

  onMouseDown(event) {
    const originalMouseX = event.pageX;
    const originalMouseY = event.pageY;
    const { cursor } = event.target.style;
    const originalHeight = this._wrapper.offsetHeight;
    const originalWidth = this._wrapper.offsetWidth;
    const originalLeft = parseFloat(this._wrapper.style.left.replace('px', ''));
    const customResize = moveEvent => {
      if (includes([CURSOR_TYPES.TOP, CURSOR_TYPES.TOP_LEFT, CURSOR_TYPES.TOP_RIGHT], cursor)) {
        const height = originalHeight - (moveEvent.pageY - originalMouseY);
        if (height >= CONTAINER_HEIGHT + TIP_HEIGHT) {
          this._wrapper.style.height = `${height}px`;
          this._wrapper.style.top = `${-height}px`;
          this._contentNode.style.height = `${height - CONTAINER_Y_OFFSET}px`;
        }
      }
      let width;
      const deltaMouseX = moveEvent.pageX - originalMouseX;
      if (includes([CURSOR_TYPES.LEFT, CURSOR_TYPES.TOP_LEFT], cursor)) {
        width = originalWidth - deltaMouseX;
        if (width >= CONTAINER_WIDTH) {
          this._wrapper.style.width = `${width}px`;
          this._wrapper.style.left = `${originalLeft + deltaMouseX}px`;
          this._contentNode.style.width = `${width - CONTAINER_X_OFFSET}px`;
        }
      }
      if (includes([CURSOR_TYPES.RIGHT, CURSOR_TYPES.TOP_RIGHT], cursor)) {
        width = originalWidth + deltaMouseX;
        if (width >= CONTAINER_WIDTH) {
          this._wrapper.style.width = `${width}px`;
          this._contentNode.style.width = `${width - CONTAINER_X_OFFSET}px`;
        }
      }
    };
    const stopCustomResize = () => {
      const { offsetHeight, offsetWidth } = this._wrapper;
      if (offsetHeight >= CONTAINER_HEIGHT + TIP_HEIGHT) {
        generalHeight = offsetHeight;
      }
      if (offsetWidth >= CONTAINER_WIDTH) {
        generalWidth = offsetWidth;
      }

      window.removeEventListener('mousemove', customResize);
    };
    window.addEventListener('mousemove', customResize);
    window.addEventListener('mouseup', stopCustomResize);
  }
});

L.customPopup = (opts, source) => new L.CustomPopup(opts, source);
